// Decision Graph i18n keys. English is the primary bundle; other languages fall
// back to English via i18n fallbackLng (consistent with the rest of the app).
// Every user-facing string in the Decision Graph components resolves through one
// of these keys via t() — no hardcoded literals in the components.
const en = {
  title: "Decision Graph",
  selectScenario: "Select a scenario",
  loading: "Loading…",
  loadError: "Could not load this run.",
  empty: "No canonical events to visualize.",
  stateOf: "State {{n}} / 6",
  otherRuns: "Other runs…",
  reconnecting: "Reconnecting…",
  authLost: "Authorization lost — please sign in again",
  live: {
    start: "Start live demo Run",
    tailing: "Live — tailing Run {{id}}",
    waiting: "waiting for the first event",
    lastEvent: "last event {{s}}s ago",
    marker: "Live",
    badgeSuffix: "live",
  },
  demo: {
    start: "Investor demo",
    hint: "One click: starts a live demo Run and lets it drive the graph hands-off to the outcome",
    chip: "Investor demo · {{scenario}} · hands-off, all events live",
  },
  snapshot: {
    label: "Snapshot · seq {{seq}}",
    chip: "Snapshot · seq {{seq}}",
    frozen: "A point-in-time frame of this Run, rendered under the legend version recorded at Run creation.",
    liveContinued: "This Run has continued past this point — this frame is not the current state.",
    openLive: "Open live view",
    frameMissing: "The requested sequence is not on this Run's record; showing the nearest earlier frame.",
    share: "Copy a link to this frame",
    copyFallback: "Copy this frame link",
  },
  terminal: {
    failed: "Run failed",
    cancelled: "Run cancelled",
    reason: "Reason: {{reason}}",
    reasonUnavailable: "Reason: Unavailable",
    note: "This Run did not reach an accepted outcome. The graph shows the record as it stands — nothing is inferred beyond it.",
  },
  states: {
    normal: { title: "Normal operations", desc: "Baseline steady-state posture." },
    event: { title: "Event registered", desc: "A capacity / security event enters the record." },
    mas: { title: "MAS mobilized", desc: "The multi-agent system is mobilized to respond." },
    compare: { title: "Compare candidates", desc: "Candidate solutions are generated and compared." },
    verify: { title: "Verify & accept", desc: "The decision is verified and a human accepts it." },
    outcome: { title: "Reveal outcome", desc: "The accepted outcome is committed to the record." },
  },
  chips: {
    engineUnavailable: "Engine: Unavailable",
    engine: "{{engineClass}} · {{engine}}",
    quantumVerified: "Quantum lift verified",
    quantumGated: "D-Wave: dry-run (gated)",
    integrityVerified: "Integrity verified",
    integrityUnverified: "Integrity unverified",
    demoData: "Demo data",
  },
  controls: { play: "Play", pause: "Pause", restart: "Restart" },
  rail: {
    outcome: "Outcome",
    engineClass: "Engine class",
    selectedEngine: "Selected engine",
    candidatesCompared: "Candidates compared",
    agentsMobilized: "Agents mobilized",
    unavailable: "Unavailable",
    evidence: "Evidence ({{count}})",
    approvals: "Approvals ({{count}})",
    integrity: "Integrity",
    eventChain: "Event chain",
    verified: "verified ✓",
    unverified: "unverified ✗",
    renderer: "Renderer",
    rendererWebgl2: "WebGL2",
    rendererSoftware: "WebGL2 (software)",
    rendererFallback: "2D fallback ({{reason}})",
    noEvidence: "No evidence records — Unavailable.",
    noApprovals: "No approvals — Unavailable.",
    confidenceUnavailable: "confidence: Unavailable",
    activityUnavailable: "activity: Unavailable",
    verifierUnavailable: "verifier: Unavailable",
    acceptedBy: "accepted by {{approver}}",
    humanAccepted: "A human accepted this decision — this is not an autonomous action.",
    noQuantumExplainer: "No verified D-Wave quantum lift on this record — the Leap sampler is gated to a $0 dry run, so a classical engine was selected. No Quantum badge is shown.",
  },
  legend: {
    title: "Legend",
    version: "Legend v{{v}}",
    gold: "Normal / steady-state",
    red: "Registered stress event",
    cyan: "Solver route (Evidence-backed)",
    green: "Accepted route (Approval-backed)",
    blue: "Candidate (unverified)",
    white: "Verified classical candidate",
    gray: "Unavailable / unauthorized / filtered",
  },
  solver: {
    title: "Candidate comparison",
    treatment: "Treatment",
    engineClass: "Class",
    objective: "Objective",
    status: "Status",
    selected: "Selected",
    infeasible: "Infeasible",
    gated: "Dry-run gated",
    feasible: "Feasible",
    subtitle: "State 4 — full multi-treatment comparison",
  },
  agents: {
    title: "Agent clusters",
    inspectHint: "Select a cluster to inspect its agents",
    cluster: "Cluster",
    members: "{{count}} agents",
    close: "Close",
    zoomHint: "Zoom in to expand clusters into individual agents",
  },
  quantum: {
    title: "Quantum contribution",
    gatedNote: "D-Wave Leap is gated to a $0 dry run; no verified quantum values exist.",
    objective: "Quantum objective",
    verified_lift: "Verified lift",
    chain_break: "Chain-break fraction",
    anneal_time: "Anneal time",
    embedding: "Problem embedding",
    sampler_state: "Sampler state",
    samplerStates: {
      dry_run_gated: "Dry-run gated",
      leap_hybrid: "Leap hybrid",
    },
    reveal: {
      verified: "Verified D-Wave lift",
      development: "Development signal only",
      none: "Run with no lift",
      verifiedNote: "A held-out-confirmed quantum-lift Evidence record backs this reveal.",
      developmentNote: "A development signal exists but has not passed held-out confirmation — no lift is claimed.",
      noneNote: "No verified quantum lift on this record — the Leap sampler is gated ($0 dry run) and a classical engine was selected.",
    },
  },
  realized: {
    title: "Realized outcome",
    predicted: "Predicted savings",
    realized: "Realized savings",
    delta: "Prediction error",
    note: "Realized values are measured from a verified outcome Evidence record for this run.",
    pending: "Realized savings stay Unavailable until a measured outcome Evidence record is logged (R3 join-logging). Predicted values are modeled projections.",
    basis: {
      measured: "Measured",
      modeled: "Modeled",
      unavailable: "Unavailable",
    },
  },
  accessible: {
    notice: "3D rendering is unavailable in this client{{reason}}. Showing the accessible 2D summary.",
    reasonSuffix: " ({{reason}})",
    sourceGroup: "Incident source",
    agentGroup: "Mobilized agents",
    candidateGroup: "Candidate solutions",
    decisionGroup: "Accepted decision",
    current: "current",
    notYet: "not yet in play",
    nodeCount: "{{count}} node",
    nodeCount_plural: "{{count}} nodes",
    regionLabel: "Accessible decision graph summary",
  },
};

const decisionGraphTranslations = { en };
export default decisionGraphTranslations;
